import { useCallback, useState } from 'react';
import type {
  WizardData,
  WizardDataContext,
  WizardStepName,
  WizardStepProps,
} from './types';

export type UseWizardSetup = Omit<WizardData, 'title'> & {
  onClose: () => void;
};

type WizardStepState = {
  done: boolean;
  context: WizardDataContext;
};

export type WizardState = {
  currentIndexState: number;
  stepState: WizardStepState[];
};

/**
 * useWizard hook
 * This is controller for the wizard modal
 */
export const useWizard = ({
  steps = [],
  onClose,
  hideVirtualLastStep,
}: UseWizardSetup) => {
  const countSteps = steps.length;
  const [state, setState] = useState<WizardState>({
    currentIndexState: 0,
    stepState: [],
  });
  const { currentIndexState } = state;

  /**
   * Get current step
   */
  const currentStep = steps[currentIndexState];

  /**
   * Determine if current step is first or last
   */
  const isFirstStep = currentIndexState === 0;

  /**
   * Determine if current step is first or last
   */
  const isLastStep = currentIndexState === countSteps - 1;

  /**
   * List of previous steps index
   * This list is used to determine previous steps for the current step
   */
  const previousStepsIndex = Array.from(
    { length: currentIndexState },
    (_, i) => i,
  );

  /**
   * List of next steps index
   * This list is used to determine next steps for the current step
   */
  const nextStepsIndex = Array.from(
    { length: countSteps - currentIndexState },
    (_, i) => i,
  );

  /**
   * Total steps in the wizard
   * If hideVirtualLastStep is true, then total steps will be equal to steps length
   */
  const totalSteps = hideVirtualLastStep ? steps.length : steps.length + 1;

  /**
   * List of done steps index
   * This list is used to determine which steps are already done
   */
  const doneStepsIndex = state.stepState
    .map((step, index) => (step.done ? index : -1))
    .filter(index => index !== -1);

  /**
   * List of available steps index
   * This list is used to determine which steps are available for navigation
   */
  const availableStepsIndex = [...doneStepsIndex, doneStepsIndex.length];

  /**
   * Get index of the step by alias or index
   * @param name - alias or index of the step
   */
  const getIndex = (name?: WizardStepName) => {
    if (typeof name === 'number') {
      return name;
    }

    if (typeof name === 'string') {
      return steps.findIndex(step => step.alias === name);
    }

    return currentIndexState;
  };

  /**
   * Get state of the step by alias or index
   * @param name - alias or index of the step
   */
  const getStepState = (name: WizardStepName) => {
    const index = getIndex(name);
    return (
      state.stepState[index] || {
        done: false,
        context: {},
      }
    );
  };

  /**
   * Get context of the step by alias or index
   * @param name - alias or index of the step
   */
  const getStepContext = (name: WizardStepName) =>
    getStepState(getIndex(name)).context;

  /**
   * Mark step as done
   * @see WizardActions.stepDone
   */
  const stepDone = (name?: WizardStepName) => {
    const stepIndex = getIndex(name);

    setState(prevState => {
      const { stepState } = prevState;
      stepState[stepIndex] = { ...stepState[stepIndex], done: true };
      return { ...prevState, stepState };
    });
  };

  /**
   * Go to the next step
   * @param stepContext - update context data for current step (not next step)
   * @see WizardActions.goToNextStep
   */
  const goToNextStep = (stepContext?: WizardDataContext) => {
    if (isLastStep) {
      return;
    }

    setState(prevState => {
      const { stepState } = prevState;
      if (stepContext) {
        stepState[currentIndexState] = {
          ...stepState[currentIndexState],
          context: stepContext,
        };
      }

      return {
        ...prevState,
        currentIndexState: prevState.currentIndexState + 1,
        stepState,
      };
    });
  };

  /**
   * Go to the previous step
   * @param stepContext - update context data for current step (not previous step)
   * @see WizardActions.goToPrevStep
   */
  const goToPrevStep = (stepContext?: WizardDataContext) => {
    if (isFirstStep) {
      return;
    }

    setState(prevState => {
      const { stepState } = prevState;
      if (stepContext) {
        stepState[currentIndexState] = {
          ...stepState[currentIndexState],
          context: stepContext,
        };
      }

      return {
        ...prevState,
        currentIndexState: prevState.currentIndexState - 1,
        stepState,
      };
    });
  };

  /**
   * Step data for the current step
   * @see WizardActions.stepData
   */
  const stepData = useCallback(
    (stepContext: WizardDataContext) => {
      setState(prevState => {
        const { stepState } = prevState;
        if (stepContext) {
          stepState[currentIndexState] = {
            ...stepState[currentIndexState],
            context: stepContext,
          };
        }

        return {
          ...prevState,
          stepState,
        };
      });
    },
    [currentIndexState],
  );

  /**
   * Go to the step by alias or index
   * @param name - alias or index of the step
   * @see WizardActions.goToStep
   */
  const goToStep = (name: WizardStepName) => {
    if (currentIndexState === getIndex(name)) {
      return;
    }

    if (!availableStepsIndex.includes(getIndex(name))) {
      throw new Error(`Step ${name} is not available`);
    }

    const index = getIndex(name);
    setState(prevState => ({
      ...prevState,
      currentIndexState: index,
    }));
  };

  /**
   * Prepare wizard context for the current step
   * This context is a combination of all previous steps contexts
   * @see WizardStepProps.wizardContext
   */
  const wizardContext = previousStepsIndex.map(getStepContext).reduce(
    (acc, context) => ({
      ...acc,
      ...context,
    }),
    {},
  );

  /**
   * Reset step to initial state
   * @param name - alias or index of the step
   * @see WizardActions.resetStep
   */
  const resetStep = (name?: WizardStepName) => {
    const stepIndex = getIndex(name);

    setState(prevState => {
      const { stepState } = prevState;
      stepState[stepIndex] = { done: false, context: {} };

      if (nextStepsIndex.length > 0) {
        nextStepsIndex.forEach(i => {
          stepState[i] = { done: false, context: {} };
        });
      }

      return {
        ...prevState,
        currentIndexState: stepIndex,
        stepState,
      };
    });
  };

  /**
   * Prepare contexts for all steps
   * This context is used to pass all previous steps contexts to the steps
   * @see WizardStepProps.wizardStepsContexts
   */
  const wizardStepsContexts = state.stepState.reduce(
    (acc, step, index) => ({
      ...acc,
      [steps[index].alias || index]: step.context,
    }),
    {},
  );

  /**
   * Close wizard
   * @see WizardActions.close
   */
  const close = () => {
    resetStep(0);
    onClose();
  };

  /**
   * Prepare state of the wizard for the current step
   */
  const currentState: WizardStepProps = {
    label: currentStep.label,
    stepIndex: currentIndexState,
    totalSteps,
    isLastStep,
    isFirstStep,
    isDone: getStepState(currentIndexState).done,
    context: getStepState(currentIndexState).context,
    wizardContext,
    wizardStepsContexts,
    resetStep,
    goToStep,
    goToPrevStep,
    goToNextStep,
    stepData,
    stepDone,
    close,
  };

  return {
    totalSteps,
    currentStepLabel: currentStep.label,
    nextStepLabel: steps[currentIndexState + 1]?.label || 'common:ready',
    currentStepIndex: currentIndexState,
    currentState,
    CurrentStepComponent: currentStep.content,
    doneStepsIndex,
    availableStepsIndex,
    close,
  };
};
